import { useEffect, useMemo, useState } from "react";
import { http, fmtINR, fmtPct } from "@/lib/api";

export default function Targets() {
    const [rows, setRows] = useState([]);
    const [region, setRegion] = useState("All");

    useEffect(() => {
        http.get("/targets/leaderboard").then((r) => setRows(r.data));
    }, []);

    const regions = useMemo(() => Array.from(new Set(rows.map((r) => r.region))).filter(Boolean), [rows]);
    const filtered = useMemo(
        () => (region === "All" ? rows : rows.filter((r) => r.region === region)),
        [rows, region]
    );

    const totalTarget = filtered.reduce((s, r) => s + r.target, 0);
    const totalAchieved = filtered.reduce((s, r) => s + r.achieved, 0);
    const totalPct = totalTarget ? (totalAchieved / totalTarget) * 100 : 0;

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Targets</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Store Performance Leaderboard</h1>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    Aggregate: {fmtINR(totalAchieved)} / {fmtINR(totalTarget)} · {fmtPct(totalPct)}
                </div>
            </div>

            <div className="flex items-center gap-1 rounded-sm border border-border bg-card p-0.5 w-fit">
                {["All", ...regions].map((r) => (
                    <button
                        key={r}
                        data-testid={`target-region-${r}`}
                        onClick={() => setRegion(r)}
                        className={
                            "rounded-sm px-2.5 py-1 text-[11px] font-medium transition-colors " +
                            (region === r
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        {r}
                    </button>
                ))}
            </div>

            <div className="overflow-hidden rounded-sm border border-border bg-card" data-testid="targets-table">
                <div className="max-h-[calc(100vh-260px)] overflow-auto">
                    <table className="w-full text-[12px]">
                        <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold">Rank</th>
                                <th className="px-3 py-2 text-left font-semibold">Store</th>
                                <th className="px-3 py-2 text-left font-semibold">Region</th>
                                <th className="px-3 py-2 text-left font-semibold">City</th>
                                <th className="px-3 py-2 text-right font-semibold">Target</th>
                                <th className="px-3 py-2 text-right font-semibold">Achieved</th>
                                <th className="px-3 py-2 text-right font-semibold">%</th>
                                <th className="px-3 py-2 text-left font-semibold w-64">Progress</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r, i) => (
                                <tr
                                    key={r.store_id}
                                    data-testid={`target-row-${r.store_id}`}
                                    className="border-t border-border hover:bg-muted/40"
                                >
                                    <td className="mono-num px-3 py-1.5 text-muted-foreground">
                                        {String(i + 1).padStart(2, "0")}
                                    </td>
                                    <td className="px-3 py-1.5 font-medium">{r.store}</td>
                                    <td className="px-3 py-1.5 text-muted-foreground">{r.region}</td>
                                    <td className="px-3 py-1.5 text-muted-foreground">{r.city}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.target)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right font-medium">{fmtINR(r.achieved)}</td>
                                    <td
                                        className={
                                            "mono-num px-3 py-1.5 text-right font-semibold " +
                                            (r.pct >= 100
                                                ? "text-emerald-600"
                                                : r.pct >= 80
                                                ? ""
                                                : "text-red-500")
                                        }
                                    >
                                        {fmtPct(r.pct)}
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
                                            <div
                                                className={
                                                    "h-full " +
                                                    (r.pct >= 100
                                                        ? "bg-emerald-500"
                                                        : r.pct >= 80
                                                        ? "bg-primary"
                                                        : "bg-red-400")
                                                }
                                                style={{ width: `${Math.min(120, r.pct)}%` }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
